import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";

const Header = () => {
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setMobileMenuOpen(false);
  };

  const navItems = [
    { id: "intro", key: "nav.intro" },
    { id: "principles", key: "nav.principles" },
    { id: "definitions", key: "nav.definitions" },
    { id: "reporting", key: "nav.reporting" },
    { id: "officer", key: "nav.officer" },
    { id: "risks", key: "nav.risks" },
    { id: "recruitment", key: "nav.recruitment" },
    { id: "conduct", key: "nav.conduct" },
    { id: "training", key: "nav.training" },
    { id: "downloads", key: "nav.downloads" },
  ];

  return (
    <>
      {/* Top bar */}
      <div className="bg-[#1a1a2e] text-white text-sm py-2 px-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <span className="text-[#E8792B] font-medium cursor-pointer hover:underline transition-colors">
            {t("siteConfig.topBarBack")}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-gray-300 text-xs">{t("siteConfig.topBarLabel")}</span>
        </div>
      </div>

      {/* Main header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled ? "bg-white/95 backdrop-blur-md shadow-md" : "bg-white shadow-sm"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-4 flex items-center justify-between gap-3">
          {/* Logo */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-[#1a1a2e] font-bold text-sm lg:text-base whitespace-nowrap">
                {t("siteConfig.siteName")}
              </span>
              <div className="w-px h-5 bg-gray-300 hidden xl:block" />
              <span className="text-[#E8792B] font-bold text-[11px] xl:text-xs hidden xl:inline whitespace-nowrap">
                {t("siteConfig.tagline")}
              </span>
            </div>
          </div>

          {/* Desktop Nav — tight layout so all items fit */}
          <nav className="hidden lg:flex items-center gap-0 flex-1 justify-center min-w-0 overflow-hidden">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                data-testid={`nav-${item.id}`}
                className="px-1.5 py-2 text-[11px] font-medium text-gray-700 hover:text-[#E8792B] transition-colors duration-200 rounded-md hover:bg-orange-50 whitespace-nowrap"
              >
                {t(item.key)}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2 flex-shrink-0">
            <LanguageSwitcher />
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 rounded-md hover:bg-gray-100 transition-colors"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ${
            mobileMenuOpen ? "max-h-[600px] border-t" : "max-h-0"
          }`}
        >
          <nav className="px-6 py-4 space-y-1 bg-white">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="block w-full ltr:text-left rtl:text-right px-4 py-3 text-sm font-medium text-gray-700 hover:text-[#E8792B] hover:bg-orange-50 rounded-md transition-colors"
              >
                {t(item.key)}
              </button>
            ))}
          </nav>
        </div>
      </header>
    </>
  );
};

export default Header;
